import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  FileText, ClipboardList, CheckCircle2, Calendar, Download, Plus,
  TrendingUp, ArrowRight, Clock, Eye, Edit2, Trash2, MoreVertical,
  CalendarPlus, UserPlus, Car, Wallet, Users as UsersIcon, MapPin, RotateCcw,
  AlertTriangle, Package, ExternalLink
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import JobCardModal from "@/components/JobCardModal";
import NotesHistoryModal from "@/components/NotesHistoryModal";
import InvoiceModal from "@/components/InvoiceModal";

const formatINR = (amount) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);

const STATUS_COLORS = {
  "Pending": { bg: "bg-[#FFF3E0]", text: "text-[#E65100]", dot: "bg-[#FF9800]" },
  "In Progress": { bg: "bg-[#EDE7F6]", text: "text-[#4527A0]", dot: "bg-[#7C4DFF]" },
  "Completed": { bg: "bg-[#E8F5E9]", text: "text-[#2E7D32]", dot: "bg-[#4CAF50]" },
  "Delivered": { bg: "bg-[#E0F7FA]", text: "text-[#00695C]", dot: "bg-[#26A69A]" },
};

const KPI_CONFIG = [
  { key: "All", label: "Total Jobs", iconBg: "bg-[#3B82F6]", icon: FileText },
  { key: "Pending", label: "Pending", iconBg: "bg-[#FF9800]", icon: ClipboardList },
  { key: "In Progress", label: "In Progress", iconBg: "bg-[#7C4DFF]", icon: ClipboardList },
  { key: "Completed", label: "Completed", iconBg: "bg-[#4CAF50]", icon: CheckCircle2 },
  { key: "Delivered", label: "Delivered", iconBg: "bg-[#26A69A]", icon: CheckCircle2 },
];

const QUICK_ACTIONS = [
  { label: "New Booking", icon: CalendarPlus, color: "text-[#3B82F6]", bg: "bg-blue-50", path: "/bookings/new" },
  { label: "Walk-in", icon: UsersIcon, color: "text-[#3B82F6]", bg: "bg-blue-50", path: "/bookings/new" },
  { label: "New Job Card", icon: FileText, color: "text-[#3B82F6]", bg: "bg-blue-50", action: "newJobCard" },
  { label: "Add Customer", icon: UserPlus, color: "text-[#3B82F6]", bg: "bg-blue-50", path: "/parties" },
  { label: "Add Vehicle", icon: Car, color: "text-[#3B82F6]", bg: "bg-blue-50", path: "/vehicles" },
  { label: "Add Expense", icon: Wallet, color: "text-[#3B82F6]", bg: "bg-blue-50", path: "/reports" },
];

const DashboardPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const initialFilter = searchParams.get("status") || "All";

  const [jobCards, setJobCards] = useState([]);
  const [filteredJobCards, setFilteredJobCards] = useState([]);
  const [activeFilter, setActiveFilter] = useState(initialFilter === "all" ? "All" : initialFilter.replace(/(^\w)/,(m)=>m.toUpperCase()).replace(/_/g,' '));
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [selectedJobCard, setSelectedJobCard] = useState(null);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [invoiceJobCard, setInvoiceJobCard] = useState(null);
  const [invoiceMap, setInvoiceMap] = useState({});
  const [inventoryAlerts, setInventoryAlerts] = useState([]);
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [dateError, setDateError] = useState("");
  const highlightJc = searchParams.get("highlight");

  // Sync URL param to filter
  useEffect(() => {
    const statusParam = searchParams.get("status");
    if (statusParam) {
      const mapped = statusParam === "all" ? "All" : statusParam.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
      if (KPI_CONFIG.some(k => k.key === mapped)) setActiveFilter(mapped);
    }
  }, [searchParams]);

  const fetchJobCards = useCallback(async () => {
    try { const r = await axios.get(`${API}/jobcards`); setJobCards(r.data); }
    catch { toast.error("Failed to fetch job cards"); }
    finally { setLoading(false); }
  }, []);

  const fetchInvoices = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/invoices`);
      const map = {}; r.data.forEach(inv => { map[inv.jobcard_id] = inv; }); setInvoiceMap(map);
    } catch (err) { console.error("Failed to fetch invoices:", err); }
  }, []);

  const fetchInventoryAlerts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/inventory`);
      const alerts = r.data.filter(i => i.current_stock <= i.low_stock_alert).slice(0, 5).map(i => ({
        id: i.id, item: i.item_name, count: i.current_stock, severity: i.current_stock <= 0 ? "critical" : "warning"
      }));
      setInventoryAlerts(alerts);
    } catch (err) { console.error("Failed to fetch inventory:", err); }
  }, []);

  const fetchUpcomingBookings = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/bookings/upcoming/list`);
      setUpcomingBookings(r.data);
    } catch (err) { console.error("Failed to fetch upcoming bookings:", err); }
  }, []);

  const filterJobCards = useCallback(() => {
    let filtered = jobCards;
    if (activeFilter !== "All") filtered = filtered.filter(j => j.status === activeFilter);
    if (dateRange.start || dateRange.end) {
      filtered = filtered.filter(card => {
        const d = new Date(card.submitted_date);
        const s = dateRange.start ? new Date(dateRange.start) : null;
        const e = dateRange.end ? new Date(dateRange.end) : null;
        if (s && e) return d >= s && d <= e;
        if (s) return d >= s;
        if (e) return d <= e;
        return true;
      });
    }
    setFilteredJobCards(filtered);
  }, [jobCards, activeFilter, dateRange]);

  useEffect(() => { fetchJobCards(); fetchInvoices(); fetchInventoryAlerts(); fetchUpcomingBookings(); }, [fetchJobCards, fetchInvoices, fetchInventoryAlerts, fetchUpcomingBookings, location.key]);
  useEffect(() => { filterJobCards(); }, [filterJobCards]);

  // Date range validation
  const handleDateStart = (val) => {
    if (dateRange.end && val > dateRange.end) {
      setDateError("From Date cannot be later than To Date");
      return;
    }
    setDateError("");
    setDateRange(p => ({ ...p, start: val }));
  };
  const handleDateEnd = (val) => {
    if (dateRange.start && val < dateRange.start) {
      setDateError("To Date cannot be earlier than From Date");
      return;
    }
    setDateError("");
    setDateRange(p => ({ ...p, end: val }));
  };

  const handleKPIClick = (key) => {
    setActiveFilter(key);
    const param = key === "All" ? "" : key.toLowerCase().replace(/\s+/g, "_");
    navigate(param ? `/job-cards?status=${param}` : "/job-cards", { replace: true });
  };

  const handleExport = async () => {
    try {
      const r = await axios.get(`${API}/jobcards/export/excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', `job_cards_${Date.now()}.xlsx`);
      document.body.appendChild(link); link.click(); link.remove();
      toast.success("Excel file downloaded");
    } catch { toast.error("Failed to export"); }
  };

  const handleDelete = async (cardId) => {
    if (!window.confirm("Delete this job card?")) return;
    try { await axios.delete(`${API}/jobcards/${cardId}`); toast.success("Deleted"); fetchJobCards(); fetchInvoices(); }
    catch { toast.error("Failed to delete"); }
  };

  const handleStatusChange = async (cardId, newStatus) => {
    try { await axios.put(`${API}/jobcards/${cardId}`, { status: newStatus }); toast.success("Status updated"); fetchJobCards(); }
    catch { toast.error("Failed to update status"); }
  };

  const counts = useMemo(() => {
    const c = { All: jobCards.length, Pending: 0, "In Progress": 0, Completed: 0, Delivered: 0 };
    jobCards.forEach(j => { if (c[j.status] !== undefined) c[j.status]++; });
    return c;
  }, [jobCards]);

  const todaysSchedule = useMemo(() => {
    const today = new Date().toISOString().split("T")[0];
    return jobCards.filter(j => j.submitted_date === today).slice(0, 5);
  }, [jobCards]);

  const upcomingDays = useMemo(() => {
    const days = [];
    const today = new Date();
    for (let i = 0; i < 7; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      const dateStr = d.toISOString().split("T")[0];
      const bookingCount = upcomingBookings.filter(b => b.date === dateStr).length;
      const jobCardCount = jobCards.filter(j => j.submitted_date === dateStr).length;
      days.push({
        day: i === 0 ? "Today" : d.toLocaleDateString("en-US", { weekday: "short" }),
        date: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        count: bookingCount + jobCardCount, bookingCount, jobCardCount, isToday: i === 0, dateStr
      });
    }
    return days;
  }, [jobCards, upcomingBookings]);

  const scheduleTime = (idx) => {
    const times = ["09:00 AM", "10:30 AM", "12:00 PM", "02:00 PM", "04:00 PM"];
    return times[idx % 5];
  };

  const formatDate = (d) => {
    if (!d) return "-";
    return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  return (
    <div className="p-6">
      <div className="flex gap-6">
        {/* Left main content */}
        <div className="flex-1 min-w-0">
          {/* KPI Cards - Interactive / Clickable */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
            {KPI_CONFIG.map((kpi) => {
              const Icon = kpi.icon;
              const isActive = activeFilter === kpi.key;
              return (
                <button
                  key={kpi.key}
                  data-testid={`kpi-${kpi.key.toLowerCase().replace(" ", "-")}`}
                  onClick={() => handleKPIClick(kpi.key)}
                  className={`bg-white rounded-2xl p-4 text-left transition-all shadow-sm hover:shadow-md ${isActive ? "ring-2 ring-[#3B82F6] ring-offset-1" : "border border-[#F1F5F9]"}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className={`${kpi.iconBg} w-10 h-10 rounded-xl flex items-center justify-center`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    {kpi.key === "All" && (
                      <span className="text-[11px] text-[#4CAF50] font-medium flex items-center gap-0.5">
                        <TrendingUp className="w-3 h-3" /> 12%
                      </span>
                    )}
                  </div>
                  <p className="text-[12px] text-[#94A3B8] font-medium">{kpi.label}</p>
                  <p className="text-[28px] font-bold text-[#1E293B] leading-tight">{counts[kpi.key]}</p>
                  {kpi.key !== "All" && (
                    <p className="text-[11px] text-[#3B82F6] font-medium mt-1 flex items-center gap-0.5">
                      View details <ArrowRight className="w-3 h-3" />
                    </p>
                  )}
                </button>
              );
            })}
          </div>

          {/* Filter section with date validation */}
          <div className="bg-white rounded-2xl p-4 mb-6 shadow-sm border border-[#F1F5F9]">
            <div className="flex items-end gap-4 flex-wrap">
              <div className="min-w-[150px]">
                <Label className="text-[12px] text-[#94A3B8] mb-1 block">Location</Label>
                <Select defaultValue="all">
                  <SelectTrigger data-testid="filter-location" className="h-9 text-[13px] bg-[#F8FAFC] border-[#E2E8F0]">
                    <SelectValue placeholder="All Locations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Locations</SelectItem>
                    <SelectItem value="loc1">Location 1</SelectItem>
                    <SelectItem value="loc2">Location 2</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-[150px]">
                <Label className="text-[12px] text-[#94A3B8] mb-1 block">From Date</Label>
                <Input data-testid="date-filter-start" type="date" value={dateRange.start}
                  max={dateRange.end || undefined}
                  onChange={(e) => handleDateStart(e.target.value)}
                  className="h-9 text-[13px] bg-[#F8FAFC] border-[#E2E8F0]" />
              </div>
              <div className="min-w-[150px]">
                <Label className="text-[12px] text-[#94A3B8] mb-1 block">To Date</Label>
                <Input data-testid="date-filter-end" type="date" value={dateRange.end}
                  min={dateRange.start || undefined}
                  onChange={(e) => handleDateEnd(e.target.value)}
                  className="h-9 text-[13px] bg-[#F8FAFC] border-[#E2E8F0]" />
              </div>
              {dateError && <p className="text-[11px] text-[#DC2626] pb-1">{dateError}</p>}
              {(dateRange.start || dateRange.end) && (
                <button data-testid="clear-date-filter" onClick={() => { setDateRange({ start: "", end: "" }); setDateError(""); }}
                  className="text-[13px] text-[#3B82F6] font-medium flex items-center gap-1 pb-1">
                  <RotateCcw className="w-3.5 h-3.5" /> Clear Filters
                </button>
              )}
              <Button data-testid="apply-filter-btn" onClick={() => filterJobCards()}
                className="bg-[#3B82F6] hover:bg-[#2563EB] text-white h-9 px-5 text-[13px] font-semibold rounded-lg">
                Apply Filter
              </Button>
            </div>
          </div>

          {/* Upcoming Bookings */}
          <div className="bg-white rounded-2xl p-5 mb-6 shadow-sm border border-[#F1F5F9]">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[16px] font-bold text-[#1E293B]">Upcoming Bookings (Next 7 Days)</h2>
              <button data-testid="view-calendar-link" onClick={() => navigate("/bookings?view=calendar")} className="text-[12px] text-[#3B82F6] font-medium hover:underline">View Calendar</button>
            </div>
            <div className="grid grid-cols-7 gap-2">
              {upcomingDays.map((d) => (
                <div key={d.dateStr} data-testid={`upcoming-day-${d.dateStr}`}
                  onClick={() => navigate(`/bookings?view=calendar`)}
                  className={`rounded-xl p-3 text-center transition-all cursor-pointer ${
                    d.isToday ? "bg-[#3B82F6] text-white shadow-md" : "bg-white border border-[#E2E8F0] hover:border-[#3B82F6]"}`}>
                  <p className={`text-[12px] font-semibold ${d.isToday ? "text-blue-100" : "text-[#94A3B8]"}`}>{d.day}</p>
                  <p className={`text-[16px] font-bold mt-0.5 ${d.isToday ? "text-white" : "text-[#1E293B]"}`}>{d.date}</p>
                  <div className="flex justify-center gap-0.5 mt-2">
                    <span className={`text-[13px] font-bold ${d.isToday ? "text-white" : "text-[#475569]"}`}>{d.count}</span>
                  </div>
                  <div className="flex justify-center gap-[3px] mt-1">
                    {Array.from({ length: Math.min(d.count, 6) }).map((_, j) => (
                      <div key={`dot-${d.dateStr}-${j}`} className={`w-1.5 h-1.5 rounded-full ${d.isToday ? "bg-white/60" : ["bg-[#3B82F6]", "bg-[#4CAF50]", "bg-[#FF9800]"][j % 3]}`} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Job Cards Table */}
          <div className="bg-white rounded-2xl shadow-sm border border-[#F1F5F9] overflow-hidden">
            <div className="px-5 py-4 flex items-center justify-between border-b border-[#F1F5F9]">
              <h2 className="text-[16px] font-bold text-[#1E293B]">Job Cards</h2>
              <div className="flex gap-2">
                <Button data-testid="export-excel-button" onClick={handleExport} variant="outline"
                  className="h-8 text-[12px] gap-1.5 border-[#E2E8F0] text-[#475569]" disabled={jobCards.length === 0}>
                  <Download className="w-3.5 h-3.5" /> Export to Excel
                </Button>
                <Button data-testid="create-jobcard-button" onClick={() => { setEditingCard(null); setModalOpen(true); }}
                  className="bg-[#3B82F6] hover:bg-[#2563EB] text-white h-8 text-[12px] gap-1.5 font-semibold rounded-lg">
                  <Plus className="w-3.5 h-3.5" /> New Job Card
                </Button>
              </div>
            </div>

            {activeFilter !== "All" && (
              <div className="px-5 py-2 bg-[#F8FAFC] text-[12px] text-[#64748B] border-b border-[#F1F5F9]">
                Filtered: <span className="font-semibold text-[#1E293B]">{activeFilter}</span>
                <button onClick={() => handleKPIClick("All")} className="ml-2 text-[#3B82F6] font-medium">Clear</button>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="bg-[#F8FAFC] text-[#94A3B8] text-[11px] font-semibold uppercase tracking-wider">
                    <th className="text-left px-5 py-3">Job Card #</th>
                    <th className="text-left px-3 py-3">Customer</th>
                    <th className="text-left px-3 py-3">Contact</th>
                    <th className="text-left px-3 py-3">Vehicle</th>
                    <th className="text-left px-3 py-3">Service</th>
                    <th className="text-left px-3 py-3">Status</th>
                    <th className="text-left px-3 py-3">Submitted</th>
                    <th className="text-left px-3 py-3">Invoice</th>
                    <th className="text-right px-5 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={9} className="text-center py-16 text-[#94A3B8]">Loading...</td></tr>
                  ) : filteredJobCards.length === 0 ? (
                    <tr><td colSpan={9} className="text-center py-16">
                      <FileText className="w-12 h-12 mx-auto mb-3 text-[#E2E8F0]" />
                      <p className="text-[#94A3B8]">{activeFilter === "All" ? "No job cards yet" : `No ${activeFilter.toLowerCase()} job cards`}</p>
                    </td></tr>
                  ) : filteredJobCards.slice(0, 10).map((card) => {
                    const invoice = invoiceMap[card.id];
                    const sc = STATUS_COLORS[card.status] || STATUS_COLORS["Pending"];
                    return (
                      <tr key={card.id} data-testid={`jobcard-row-${card.job_card_number}`}
                        className={`border-b transition-colors ${
                          highlightJc === card.job_card_number
                            ? "bg-[#EFF6FF] border-[#BFDBFE] border-l-4 border-l-[#3B82F6]"
                            : "border-[#F8FAFC] hover:bg-[#FAFBFD]"
                        }`}>
                        <td className="px-5 py-3">
                          <button data-testid={`jobcard-click-${card.job_card_number}`}
                            onClick={() => { setSelectedJobCard(card); setNotesModalOpen(true); }}
                            className="text-[#3B82F6] font-semibold hover:underline">{card.job_card_number}</button>
                        </td>
                        <td className="px-3 py-3 font-medium text-[#1E293B]">{card.customer_name}</td>
                        <td className="px-3 py-3 text-[#64748B]">{card.customer_contact}</td>
                        <td className="px-3 py-3 text-[#64748B] max-w-[100px] truncate">{card.vehicle_details}</td>
                        <td className="px-3 py-3 text-[#64748B]">{card.type_of_service}</td>
                        <td className="px-3 py-3">
                          <Select value={card.status} onValueChange={(v) => handleStatusChange(card.id, v)}>
                            <SelectTrigger data-testid={`status-select-${card.job_card_number}`}
                              className={`${sc.bg} ${sc.text} h-7 w-[120px] border-0 text-[11px] font-semibold rounded-full`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {["Pending", "In Progress", "Completed", "Delivered"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-3 py-3 text-[#64748B]">{formatDate(card.submitted_date)}</td>
                        <td className="px-3 py-3">
                          {invoice ? (
                            <div className="text-[11px]">
                              <span className="text-[#3B82F6] font-semibold">{invoice.invoice_number}</span>
                              <br/>
                              <span className={invoice.status === 'Paid' ? 'text-[#4CAF50]' : 'text-[#FF9800]'}>
                                {formatINR(invoice.amount)} · {invoice.status}
                              </span>
                            </div>
                          ) : card.status === 'Delivered' ? (
                            <Button data-testid={`create-invoice-${card.job_card_number}`} size="sm" variant="outline"
                              onClick={() => { setInvoiceJobCard(card); setInvoiceModalOpen(true); }}
                              className="h-6 text-[10px] gap-0.5 text-[#3B82F6] border-[#BFDBFE]">
                              <FileText className="w-3 h-3" /> Invoice
                            </Button>
                          ) : <span className="text-[11px] text-[#CBD5E1]">-</span>}
                        </td>
                        <td className="px-5 py-3 text-right">
                          <div className="flex justify-end gap-0.5">
                            <button data-testid={`view-button-${card.job_card_number}`}
                              onClick={() => { setSelectedJobCard(card); setNotesModalOpen(true); }}
                              className="p-1.5 rounded-md hover:bg-[#F1F5F9] text-[#94A3B8] hover:text-[#475569] transition-colors">
                              <Eye className="w-4 h-4" />
                            </button>
                            <button data-testid={`edit-button-${card.job_card_number}`}
                              onClick={() => { setEditingCard(card); setModalOpen(true); }}
                              className="p-1.5 rounded-md hover:bg-[#F1F5F9] text-[#94A3B8] hover:text-[#475569] transition-colors">
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button className="p-1.5 rounded-md hover:bg-[#F1F5F9] text-[#94A3B8] hover:text-[#475569] transition-colors">
                                  <MoreVertical className="w-4 h-4" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleDelete(card.id)} className="text-red-600 gap-2">
                                  <Trash2 className="w-3.5 h-3.5" /> Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {filteredJobCards.length > 10 && (
              <div className="px-5 py-3 border-t border-[#F1F5F9] text-center">
                <button data-testid="view-all-jobcards" onClick={() => navigate("/job-cards")}
                  className="text-[12px] text-[#3B82F6] font-medium flex items-center gap-1 mx-auto hover:text-[#2563EB] hover:underline cursor-pointer transition-colors">
                  View All Job Cards <ArrowRight className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Right sidebar widgets */}
        <div className="hidden xl:block w-[280px] shrink-0 space-y-5">
          {/* Today's Schedule */}
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-[#F1F5F9]">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-[15px] font-bold text-[#1E293B]">Today's Schedule</h3>
              <Calendar className="w-4 h-4 text-[#94A3B8]" />
            </div>
            <p className="text-[11px] text-[#94A3B8] mb-4">
              {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
            </p>
            <div className="space-y-0">
              {todaysSchedule.length === 0 ? (
                <p className="text-[12px] text-[#CBD5E1] text-center py-6">No jobs scheduled for today</p>
              ) : todaysSchedule.map((jc, idx) => {
                const sc = STATUS_COLORS[jc.status] || STATUS_COLORS["Pending"];
                return (
                  <div key={jc.id} className="flex items-center gap-3 py-2.5 border-b border-[#F8FAFC] last:border-0">
                    <span className="text-[11px] text-[#94A3B8] font-medium w-16 shrink-0">{scheduleTime(idx)}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-semibold text-[#1E293B] truncate">{jc.customer_name}</p>
                      <p className="text-[10px] text-[#94A3B8] truncate">{jc.vehicle_details}</p>
                    </div>
                    <span className={`${sc.bg} ${sc.text} text-[9px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap`}>
                      {jc.status}
                    </span>
                  </div>
                );
              })}
            </div>
            <button onClick={() => navigate("/bookings?view=calendar")}
              className="text-[11px] text-[#3B82F6] font-medium mt-3 flex items-center gap-0.5 ml-auto">
              View Full Schedule <ArrowRight className="w-3 h-3" />
            </button>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-[#F1F5F9]">
            <h3 className="text-[15px] font-bold text-[#1E293B] mb-3">Quick Actions</h3>
            <div className="grid grid-cols-2 gap-2">
              {QUICK_ACTIONS.map((action) => {
                const Icon = action.icon;
                return (
                  <button key={action.label} data-testid={`quick-action-${action.label.toLowerCase().replace(/\s/g, '-')}`}
                    onClick={() => {
                      if (action.action === "newJobCard") { setEditingCard(null); setModalOpen(true); }
                      else if (action.path) navigate(action.path);
                    }}
                    className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-[#F8FAFC] hover:bg-[#EFF6FF] transition-all hover:shadow-sm group">
                    <div className={`w-9 h-9 rounded-lg ${action.bg} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                      <Icon className={`w-4 h-4 ${action.color}`} />
                    </div>
                    <span className="text-[10px] font-medium text-[#64748B] text-center leading-tight">{action.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Inventory Alerts — clickable for out-of-stock */}
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-[#F1F5F9]">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[15px] font-bold text-[#1E293B]">Inventory Alerts</h3>
              <button onClick={() => navigate("/inventory")} className="text-[11px] text-[#3B82F6] font-medium hover:underline">View All</button>
            </div>
            <div className="space-y-2.5">
              {inventoryAlerts.length === 0 ? (
                <p className="text-[12px] text-[#CBD5E1] text-center py-4">No alerts</p>
              ) : inventoryAlerts.map((alert) => (
                <button key={`alert-${alert.id}`} data-testid={`inventory-alert-${alert.item}`}
                  onClick={() => navigate(`/inventory?item_id=${alert.id}`)}
                  className="flex items-center gap-2.5 w-full text-left hover:bg-[#F8FAFC] rounded-lg p-1 -m-1 transition-colors">
                  <AlertTriangle className={`w-4 h-4 shrink-0 ${alert.severity === "critical" ? "text-[#EF4444]" : "text-[#F59E0B]"}`} />
                  <span className="text-[12px] text-[#475569] flex-1 truncate">
                    {alert.severity === "critical" ? "Out of stock" : "Low stock"}: <span className="font-medium">{alert.item}</span>
                  </span>
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${
                    alert.severity === "critical" ? "bg-[#EF4444]" : "bg-[#F59E0B]"}`}>
                    {alert.count}
                  </span>
                  {alert.severity === "critical" && <ExternalLink className="w-3 h-3 text-[#94A3B8]" />}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <JobCardModal open={modalOpen} onClose={(r) => { setModalOpen(false); setEditingCard(null); if (r) { fetchJobCards(); fetchInvoices(); }}} editingCard={editingCard} />
      <NotesHistoryModal open={notesModalOpen} onClose={() => setNotesModalOpen(false)} jobCard={selectedJobCard} />
      <InvoiceModal open={invoiceModalOpen} onClose={(r) => { setInvoiceModalOpen(false); setInvoiceJobCard(null); if (r) fetchInvoices(); }} jobCard={invoiceJobCard} />
    </div>
  );
};

export default DashboardPage;
